import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: 'website' | 'article';
  image?: string;
}

const BASE_URL = 'https://aistreambuddy.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'AI Stream Buddy';

/**
 * SEO component for managing page-specific meta tags
 * Updates document title and meta tags dynamically for each page
 */
export function SEO({ 
  title, 
  description, 
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE 
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tag
    const setMetaTag = (attr: string, key: string, content: string) => {
      let element = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update standard meta tags
    setMetaTag('name', 'description', description);
    setMetaTag('name', 'title', title);

    // Update Open Graph tags
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:image', image);
    setMetaTag('property', 'og:site_name', SITE_NAME);
    
    if (canonical) {
      setMetaTag('property', 'og:url', canonical);
      
      // Update canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Update Twitter Card tags
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', image);
    if (canonical) {
      setMetaTag('name', 'twitter:url', canonical);
    }
  }, [title, description, canonical, type, image]);

  // This component doesn't render anything
  return null;
}

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: 'Twitch AI Bot and TTS Voice | AI Stream Buddy',
    description: 'Twitch AI bot for streamers with live voice, Twitch TTS, OBS browser source workflow, push-to-talk, and reactions to chat, alerts, and gameplay.',
    canonical: BASE_URL,
  },
  about: {
    title: 'About AI Stream Buddy | Built for Twitch Streamers',
    description: 'Why AI Stream Buddy exists and how it was built for solo Twitch streamers who want a stronger live show.',
    canonical: `${BASE_URL}/about`,
  },
  demo: {
    title: 'Twitch TTS Demo and AI Voice | AI Stream Buddy',
    description: 'Try Twitch TTS style voices and AI voice reactions in a live browser demo with push-to-talk, voice selection, and real-time replies before you connect Twitch.',
    canonical: `${BASE_URL}/demo`,
  },
  faq: {
    title: 'Twitch AI Bot FAQ | Pricing, OBS Setup, Twitch Alerts',
    description: 'Answers about AI Stream Buddy pricing, Twitch alerts, OBS workflow, voice customization, moderation controls, and setup for streamers.',
    canonical: `${BASE_URL}/faq`,
  },
  guides: {
    title: 'Twitch Alerts, OBS, and TTS Guides | AI Stream Buddy',
    description: 'Guides for Twitch alerts, OBS browser sources, TTS, AI voice, push-to-talk, and smoother solo stream workflow.',
    canonical: `${BASE_URL}/guides`,
  },
  privacy: {
    title: 'Privacy Policy - AI Stream Buddy',
    description: 'How AI Stream Buddy collects, uses, and protects your data. Our commitment to streamer privacy and data security.',
    canonical: `${BASE_URL}/privacy`,
  },
  terms: {
    title: 'Terms of Service - AI Stream Buddy',
    description: 'Terms and conditions for using AI Stream Buddy. Account requirements, acceptable use, and service policies.',
    canonical: `${BASE_URL}/terms`,
  },
};
